import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, Eye, Edit, X, MoreHorizontal } from "lucide-react";
import AddServiceLocationToOnboardingSheet from "./AddClientToOnboardingSheet";
import EditClientOnboardingSheet from "./EditClientOnboardingSheet";

// Days remaining filter options
const DAYS_REMAINING_RANGES = [
  { value: "overdue", label: "Overdue", min: -Infinity, max: 0 },
  { value: "1_5", label: "1-5 days", min: 1, max: 5 },
  { value: "6_14", label: "6-14 days", min: 6, max: 14 },
  { value: "15_30", label: "15-30 days", min: 15, max: 30 },
  { value: "30_plus", label: "30+ days", min: 31, max: Infinity },
];

export default function OnboardingList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [daysRemainingFilter, setDaysRemainingFilter] = useState<string | null>(null);
  const [isAddServiceLocationSheetOpen, setIsAddServiceLocationSheetOpen] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [selectedOnboarding, setSelectedOnboarding] = useState<any>(null);

  // Fetch real onboarding data
  const onboardingRecords = useQuery(api.clientOnboarding.list);
  const isLoading = onboardingRecords === undefined;

  // Calculate days remaining for each record
  const onboardingLocations = (onboardingRecords || []).map((record: any) => {
    const dueDate = new Date(record.dueDate);
    const today = new Date();
    const timeDiff = dueDate.getTime() - today.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));

    return {
      id: record._id,
      serviceLocation: record.serviceLocationName || "Location Required",
      client: record.businessName,
      startDate: record.startDate,
      daysRemaining: record.status === "completed" ? 0 : daysRemaining,
      status:
        record.status === "not-started"
          ? "Not Started"
          : record.status === "in-progress"
            ? "In Progress"
            : "Completed",
      assignedTo: record.assignedUserName,
      progress: record.progress,
    };
  });

  // Check if any filters are active
  const hasActiveFilters = statusFilter !== null || daysRemainingFilter !== null;

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter(null);
    setDaysRemainingFilter(null);
  };

  const filteredLocations = onboardingLocations.filter((location: any) => {
    // Apply search filter
    const matchesSearch =
      searchQuery === "" ||
      location.serviceLocation.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.client.toLowerCase().includes(searchQuery.toLowerCase());

    // Apply status filter
    const matchesStatus = statusFilter === null || location.status === statusFilter;

    // Apply days remaining filter
    let matchesDaysRemaining = true;
    if (daysRemainingFilter !== null && location.status !== "Completed") {
      const range = DAYS_REMAINING_RANGES.find((r) => r.value === daysRemainingFilter);
      if (range) {
        matchesDaysRemaining =
          location.daysRemaining >= range.min && location.daysRemaining <= range.max;
      }
    }

    return matchesSearch && matchesStatus && matchesDaysRemaining;
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Not Started":
        return "bg-gray-200 text-gray-800";
      case "In Progress":
        return "bg-blue-100 text-blue-800";
      case "Completed":
        return "bg-green-100 text-green-800";
      default:
        return "";
    }
  };

  const handleEditOnboarding = (location: any) => {
    // Find the original onboarding record
    const originalRecord = onboardingRecords?.find((record: any) => record._id === location.id);
    if (originalRecord) {
      setSelectedOnboarding(originalRecord);
      setIsEditSheetOpen(true);
    }
  };

  return (
    <DashboardLayout activeTab="clients">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Service Location Onboarding</h1>
          <AddServiceLocationToOnboardingSheet
            open={isAddServiceLocationSheetOpen}
            onOpenChange={setIsAddServiceLocationSheetOpen}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Onboarding Service Locations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex flex-col gap-4 md:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search service locations or clients..."
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select
                value={statusFilter || "all"}
                onValueChange={(value) => setStatusFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="Not Started">Not Started</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={daysRemainingFilter || "all"}
                onValueChange={(value) => setDaysRemainingFilter(value === "all" ? null : value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by days" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Days Remaining</SelectItem>
                  {DAYS_REMAINING_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="gap-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-4 w-4" /> Clear Filters
                </Button>
              )}
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Service Location</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Service Start Date</TableHead>
                    <TableHead>Days Remaining</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        <div className="flex items-center justify-center">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Loading onboarding records...
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredLocations.length > 0 ? (
                    filteredLocations.map((location: any) => (
                      <TableRow key={location.id}>
                        <TableCell className="font-medium">{location.serviceLocation}</TableCell>
                        <TableCell>{location.client}</TableCell>
                        <TableCell>{location.startDate}</TableCell>
                        <TableCell>
                          {location.status === "Completed" ? (
                            "Complete"
                          ) : (
                            <span
                              className={
                                location.daysRemaining <= 5 ? "font-medium text-red-600" : ""
                              }
                            >
                              {location.daysRemaining} days
                            </span>
                          )}
                        </TableCell>
                        <TableCell>{location.assignedTo}</TableCell>
                        <TableCell>
                          <Badge className={getStatusBadgeColor(location.status)}>
                            {location.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => navigate(`/clients/onboarding/${location.id}`)}>
                                <Eye className="mr-2 h-4 w-4" />
                                View
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleEditOnboarding(location)}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="h-24 text-center">
                        No onboarding records found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Onboarding Sheet */}
      {selectedOnboarding && (
        <EditClientOnboardingSheet
          open={isEditSheetOpen}
          onOpenChange={setIsEditSheetOpen}
          onboarding={selectedOnboarding}
          onUpdated={() => {
            // Onboarding records will automatically refresh due to Convex reactivity
            setIsEditSheetOpen(false);
            setSelectedOnboarding(null);
          }}
        />
      )}
    </DashboardLayout>
  );
}
